/**
 * Didacticiel de la feuille de brouillon = l'exercice guidé de la démo.
 *
 * Un vrai exercice de la classe (l'exercice de démo du niveau, jamais celui qu'on va jouer), sur la feuille du moteur
 * v2, avec le coach générique qui montre où toucher (`features/demo/guided`). Rien n'est enregistré (ni ligne, ni
 * HORA, ni XP). Si la question ne se charge pas, ou si le coach ne sait pas la mener jusqu'à la réponse, on joue
 * l'ancien tutoriel « A = 3 × 4 + 2 × 5 », local, qui marche toujours.
 *
 * Proposé à la première prise en main (`first-use.ts`) : invité d'un défi (salon, en surimpression), fin de
 * l'inscription (page `/brouillon/tutoriel`), premier exercice (à la place de l'écran d'accueil). « Passer » à tout
 * moment ; fini ou passé, il n'est plus proposé.
 */

import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { PaperColumn, PaperPage } from '@jeraisonne/ui/paper';
import { LEVEL_LABEL } from '@/features/demo/types';
import type { GuidedQuestion } from '@/features/demo/guided/guided-question';
import { loadGuidedQuestion } from '@/features/demo/guided/guided-question';
import { trackAppEvent } from '@/lib/analytics/track';
import { markScratchTutorialFinished, tutorialExerciseFor, tutorialLevelFor } from './first-use';

// le moteur v2 (packs compilés) n'est chargé qu'ici
const GuidedRound = lazy(() => import('@/features/demo/guided/guided-round'));
const TutorialRound = lazy(() => import('./tutorial-round'));

/** Où le didacticiel est proposé (statistiques). */
export type TutorialContext = 'defi' | 'inscription' | 'exercice' | 'page';

export interface GuidedTutorialProps {
  /** classe de l'élève, du défi ou de l'exercice ; sans classe : 4e */
  level?: string | null;
  /** exercice qu'on va jouer : jamais le didacticiel */
  avoidExerciseId?: string | null;
  context: TutorialContext;
  /** une phrase propre à l'endroit (« Ce défi a des questions à résoudre sur la feuille… ») */
  intro?: string;
  /** bouton de la fin */
  doneLabel?: string;
  /** bouton du haut, toujours visible, pour sortir avant la fin (« Revenir au défi ») */
  skipLabel?: string;
  /** fini ou passé */
  onDone: () => void;
}

type Phase = 'loading' | 'guided' | 'fallback' | 'end';

export function GuidedTutorial({
  level,
  avoidExerciseId,
  context,
  intro,
  doneLabel = 'C’est parti !',
  skipLabel = 'Passer',
  onDone,
  withKit = true,
}: GuidedTutorialProps & {
  /** faux en surimpression : l'écran du dessous a déjà sa trousse (le chapitre est sauté) */
  withKit?: boolean;
}) {
  const lv = tutorialLevelFor(level);
  const [question, setQuestion] = useState<GuidedQuestion | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [round, setRound] = useState(0);
  const top = useRef<HTMLDivElement>(null);
  // un vrai retour (« Revenir au défi ») à gauche, avec sa flèche ; sinon « Passer » à droite
  const back = skipLabel !== 'Passer';

  // la fin s'affiche en haut (page ou surimpression : c'est le conteneur qui défile)
  useEffect(() => {
    if (phase === 'end') top.current?.scrollIntoView?.({ block: 'start' });
  }, [phase]);

  useEffect(() => {
    trackAppEvent('page_view', `/brouillon/didacticiel/${context}`);
  }, [context]);

  useEffect(() => {
    let cancelled = false;
    loadGuidedQuestion(tutorialExerciseFor(lv, avoidExerciseId))
      .then((q) => {
        if (cancelled) return;
        if (q?.question && q.model) {
          setQuestion(q);
          setPhase('guided');
        } else setPhase('fallback');
      })
      .catch(() => {
        if (!cancelled) setPhase('fallback');
      });
    return () => {
      cancelled = true;
    };
  }, [lv, avoidExerciseId]);

  function finish() {
    markScratchTutorialFinished();
    trackAppEvent('page_view', `/brouillon/didacticiel/${context}/fin`);
    setPhase('end');
  }
  function skip() {
    markScratchTutorialFinished();
    onDone();
  }

  const loading = (
    <p
      className="text-ink-600 mt-6 flex items-center gap-2 text-sm"
      data-testid="guided-tutorial-loading"
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      Préparation de ta feuille…
    </p>
  );

  return (
    <PaperPage>
      <PaperColumn className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-8 pt-3">
        <div ref={top} className="scroll-mt-4" />
        {/* toujours visible : le coach fait défiler la page, la sortie ne doit jamais disparaître */}
        <div
          className="native:top-[env(safe-area-inset-top)] sticky top-0 z-30 -mx-1 flex items-center gap-2 bg-[var(--sc-paper)] px-1 py-1"
          data-testid="guided-tutorial-header"
        >
          {phase !== 'end' && back && (
            <button
              type="button"
              onClick={skip}
              className="text-primary-600 hover:text-primary-700 -ml-2 inline-flex min-h-11 shrink-0 items-center gap-1 px-2 text-sm font-semibold"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {skipLabel}
            </button>
          )}
          <p className="text-primary-600 min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wider">
            {back && phase !== 'end' ? null : 'Prise en main du brouillon'}
          </p>
          {phase !== 'end' && !back && (
            <button
              type="button"
              onClick={skip}
              className="text-primary-600 hover:text-primary-700 min-h-11 shrink-0 px-2 text-sm font-semibold"
            >
              {skipLabel}
            </button>
          )}
        </div>

        {phase === 'end' ? (
          <div className="mt-6 space-y-4" data-testid="guided-tutorial-end">
            <h1 className="font-display text-ink-900 text-2xl font-extrabold leading-tight">
              Tu sais te servir du brouillon !
            </h1>
            <p className="text-ink-700 text-[15px]">
              Chaque ligne juste te rapporte des HORA. Le moteur vérifie tes calculs, repère
              l’erreur classique et te propose les valeurs : à toi de raisonner. L’ampoule est là si
              tu bloques.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button fullWidth className="min-h-11" onClick={onDone}>
                {doneLabel}
              </Button>
              <Button
                variant="outline"
                fullWidth
                className="min-h-11"
                onClick={() => {
                  setRound((r) => r + 1);
                  setPhase(question ? 'guided' : 'fallback');
                }}
              >
                <RotateCcw className="mr-2 h-4 w-4" aria-hidden />
                Recommencer
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-display text-ink-900 mt-2 text-2xl font-extrabold leading-tight">
              La feuille de brouillon en 1 minute
            </h1>
            <p className="text-ink-600 mt-1 text-[15px]">
              {intro ? `${intro} ` : ''}
              {phase === 'fallback'
                ? 'Un calcul simple, guidé pas à pas. Rien n’est noté.'
                : `Un vrai exercice de ${LEVEL_LABEL[lv]}. Suis le doré : il te montre où toucher. Rien n’est noté.`}
            </p>
            {phase === 'loading' && loading}
            {phase === 'guided' && question && (
              <Suspense fallback={loading}>
                <GuidedRound
                  key={round}
                  question={question}
                  onFinished={finish}
                  onUnavailable={() => setPhase('fallback')}
                  withKit={withKit}
                  level={lv}
                />
              </Suspense>
            )}
            {phase === 'fallback' && (
              <Suspense fallback={loading}>
                <TutorialRound
                  key={round}
                  exitLabel={doneLabel}
                  onRestart={() => setRound((r) => r + 1)}
                  onExit={() => {
                    markScratchTutorialFinished();
                    trackAppEvent('page_view', `/brouillon/didacticiel/${context}/fin`);
                    onDone();
                  }}
                />
              </Suspense>
            )}
          </>
        )}
      </PaperColumn>
    </PaperPage>
  );
}

/**
 * Le didacticiel par-dessus la page (salon d'un défi) : plein écran, il défile seul, la page dessous ne bouge pas.
 * Échap = « Passer ».
 */
export function GuidedTutorialOverlay(props: GuidedTutorialProps) {
  const { onDone } = props;
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      markScratchTutorialFinished();
      onDone();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onDone]);
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Prise en main du brouillon"
      className="fixed inset-0 z-[90] overflow-y-auto overscroll-contain"
      data-testid="guided-tutorial-overlay"
    >
      <GuidedTutorial {...props} withKit={false} />
    </div>,
    document.body,
  );
}

export default GuidedTutorial;
